import { AltLabel } from '../../TreeViewTypes';
import { DataNode, LabelContext } from '../../data_nodes/types';
import { LabelGenerator, DefaultLabelGenerator } from '../types';
import { getVariantFromResultStatus, createVariantMetadata } from '../../utils/iconVariants';

/**
 * Generate default label for Result and KeyResult nodes
 *
 * Returns: "[text] (done: [true|false])"
 */
export const generateResultDefaultLabel: DefaultLabelGenerator = (
  dataNode: DataNode<any>,
  context: LabelContext,
): string => {
  const data = dataNode.data;

  // Handle data with result
  if (data && data.result) {
    const text = data.result.text || 'Untitled Result';
    const doneStatus = data.result.delivered ? 'done' : 'not done';

    // ✅ Set icon variant based on result status
    const variant = getVariantFromResultStatus(data.result);
    if (variant) {
      dataNode.metadata = { ...dataNode.metadata, ...createVariantMetadata(variant) };
    }

    return `${text} (${doneStatus})`;
  }

  // ✅ Fallback variant for missing result data
  const variant = 'results-pending';
  dataNode.metadata = { ...dataNode.metadata, ...createVariantMetadata(variant) };

  // Fallback
  return 'Result';
};

/**
 * Generate alt labels for Result and KeyResult nodes
 *
 * Provides options for displaying result information in different formats
 */
export const generateResultAltLabels: LabelGenerator = (
  dataNode: DataNode<any>,
  context: LabelContext,
): AltLabel[] => {
  const data = dataNode.data;
  const defaultLabel = generateResultDefaultLabel(dataNode, context);

  const labels: AltLabel[] = [
    {
      key: 'default',
      selectOption: 'Default: Description & Status',
      value: defaultLabel,
    },
  ];

  // If we have result data, provide description-only option
  if (data && data.result) {
    const text = data.result.text || 'Untitled Result';

    labels.push({
      key: 'description',
      selectOption: 'Description',
      value: text,
    });
  }

  return labels;
};
